import { logError, logInfo } from 'src/loggingManager';
import { parseError } from 'src/util/misc';
import { AssetType, SceneService_AssetType_NotSaved } from 'src/types/asset';
import { RenderingSDK, AssetItemEntity } from '@rendering/web-client';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_ASSET_TYPE = 'scene';

/** Loads the content of a specific version of an asset. */
export function loadAssetUri(sdk: RenderingSDK, { assetId, versionId }: { assetId: string; versionId: string }) {
    if (!assetId || !versionId) {
        return undefined;
    }

    return sdk.Assets.getRevisionVersionContent(assetId, versionId, { 'Cache-Control': 'no-cache' }).catch((e) => {
        logError(`Load asset version content failed (assetId=${assetId}, versionId=${versionId}): ${parseError(e)}`);
        throw e;
    });
}

// Helper to parse an asset
export function parseAsset(asset: AssetItemEntity): AssetType {
    return {
        latestVersionId: asset.latestRevisionId,
        publishedVersionId: asset.publishedRevisionId,
        id: asset.id,
        name: asset.name,
        mcpSku: asset.referenceId,
        mcpSkuVersion: asset.referenceIdVersion,
        description: asset.description,
        notes: asset.notes,
        created: asset.created,
        owner: asset.owner,
        lastModified: new Date(asset.lastModified),
        modifiedBy: new Date(asset.modifiedBy),
        lastPublished: new Date(asset.lastPublished),
        publishedBy: asset.publishedBy,
        variables: asset.attributes || {},
        tenantType: asset.tenant.type,
        tenantId: asset.tenant.id,
        deleted: asset.deleted || false,
        tags: asset.tags || [],
        publishedVersions: (asset.recentPublishedRevisions || []).map((version) => ({
            id: version.revisionId,
            date: new Date(version.publishedAt),
            publisher: version.publishedBy,
        })),
    };
}

/**
 * Creates an asset
 * @param options
 * @param options.tenantType - The type of owner resource (merchant / fulfiller)
 * @param options.tenantId - The id of the resource
 * @param options.asset - The updated asset object
 * @param options.asset.assetType - The type of the asset
 * @param options.asset.name - The updated asset name
 * @param options.asset.description - The updated asset description
 * @param options.asset.referenceId - The updated sku
 * @param options.asset.attributes - The updated sku variables
 * @param options.asset.tags - string array of tags for the asset
 * @param options.asset.initialRevision - the initial version of this asset
 * @param options.asset.initialRevision.uri - the URI to the initial version's content
 * @param [options.asset.initialRevision.metadata] - the metadata for this type of asset
 * @returns The promise of the request.
 */
export function createAsset(
    sdk: RenderingSDK,
    {
        tenantType,
        tenantId,
        asset,
    }: {
        tenantType: string;
        tenantId: string;
        asset: Omit<SceneService_AssetType_NotSaved, 'tenant' | 'deleted' | 'referenceId' | 'referenceIdVersion'>;
    },
): Promise<AssetType> {
    const data = {
        ...asset,
        tenant: {
            id: tenantId,
            type: tenantType,
        },
    };
    return sdk.Assets.create(data)
        .then((response) => {
            logInfo(`Created asset: ${response} ${JSON.stringify(response || {})}`);
            return parseAsset(response);
        })
        .catch((e) => {
            logError(`Creating asset failed (data=${JSON.stringify(data)}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Updates an asset
 * @param options
 * @param options.tenantType - The type of owner resource (merchant / fulfiller)
 * @param options.tenantId - The id of the resource
 * @param options.assetId - The id of the asset
 * @param options.newAsset - The updated asset object
 * @param options.newAsset.assetType - The type of the asset
 * @param options.newAsset.name - The updated asset name
 * @param options.newAsset.description - The updated asset description
 * @param options.newAsset.referenceId - The updated sku
 * @param options.newAsset.attributes - The updated sku variables
 * @param options.newAsset.latestRevisionId - The updated latest version
 * @param options.newAsset.publishedRevisionId - The updated published version
 * @param options.newAsset.tags - string array of tags for the asset
 * @param options.newAsset.deleted - if the asset is deleted or not
 * @returns The promise of the request
 */
export function updateAsset(
    sdk: RenderingSDK,
    {
        tenantType,
        tenantId,
        assetId,
        newAsset,
    }: {
        tenantType: string;
        tenantId: string;
        assetId: string;
        newAsset: {
            assetType?: string;
            name?: string;
            description?: string;
            referenceId?: string;
            referenceIdVersion?: number;
            attributes?: Record<string, any>;
            latestRevisionId?: string;
            publishedRevisionId?: string;
            tags?: string[];
            deleted?: boolean;
            notes?: string;
        };
    },
) {
    const data = {
        ...newAsset,
        tenant: {
            id: tenantId,
            type: tenantType,
        },
    };
    return sdk.Assets.update(assetId, data)
        .then((response) => {
            logInfo(`Updated asset: ${response} ${JSON.stringify(response || {})}`);
            return parseAsset(response);
        })
        .catch((e) => {
            logError(`Updating asset failed (assetId=${assetId}) (data=${JSON.stringify(data)}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Creates an asset version
 * @param options
 * @param options.assetId - The id of the asset
 * @param options.version - The asset version object
 * @param options.version.uri - The uri to the new version's content
 * @param [options.version.metadata] - metadata for this type of asset
 * @returns The promise of the request.
 */
export function createVersion(
    sdk: RenderingSDK,
    {
        assetId,
        version,
    }: {
        assetId: string;
        version: { uri: string; metadata?: Record<string, any> };
    },
) {
    return sdk.Assets.createRevision(assetId, version.uri, version.metadata).catch((e) => {
        logError(`Creating asset version failed (data=${JSON.stringify(version)}): ${parseError(e)}`);
        throw e;
    });
}

/**
 * Get a specific version of a specific asset
 * @param options
 * @param options.assetId - The id of the asset
 * @param options.versionId - The id of the version
 * @returns The promise of the request
 */
export function getVersion(
    sdk: RenderingSDK,
    { assetId, versionId }: { assetId: string; versionId: string },
): Promise<any> {
    return sdk.Assets.getRevisionVersion(assetId, versionId, { 'Cache-Control': 'no-cache' }).catch((e) => {
        logError(`Load versioned asset failed (assetId=${assetId}, versionId=${versionId}): ${parseError(e)}`);
        throw e;
    });
}

/**
 * Gets all versions of an asset
 * @param options
 * @param options.assetId - The id of the asset.
 * @returns The promise of the request.
 */
export function getAllVersions(sdk: RenderingSDK, { assetId }: { assetId: string }) {
    return sdk.Assets.getAllRevisions(assetId, { 'Cache-Control': 'no-cache' })
        .then((response) =>
            response._embedded.item?.map((version: TODO) => ({
                id: version.id,
                created: version.created,
                creator: version.createdBy,
            })),
        )
        .catch((e) => {
            logError(`Getting all versions for asset failed (assetId=${assetId}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Gets an asset
 * @param options
 * @param options.assetId - The id of the asset
 * @returns The promise of the request
 */
export function getAsset(sdk: RenderingSDK, { assetId }: { assetId: string }) {
    return sdk.Assets.get(assetId, { 'Cache-Control': 'no-cache' })
        .then((response) => parseAsset(response))
        .catch((e) => {
            logError(`Getting asset failed (assetId=${assetId}): ${parseError(e)}`);
            throw e;
        });
}

/**
 * Delete an asset
 * @param options
 * @param options.assetId - The ID of the asset
 */
export function deleteAsset(sdk: RenderingSDK, { assetId }: { assetId: string }) {
    return sdk.Assets.remove(assetId).catch((e) => {
        logError(`Deleting asset failed (assetId=${assetId}): ${parseError(e)}`);
        throw e;
    });
}

/**
 * Restore an asset
 * @param options
 * @param options.assetId - The ID of the asset
 */
export function restoreAsset(sdk: RenderingSDK, { assetId }: { assetId: string }) {
    return sdk.Assets.restore(assetId).catch((e) => {
        logError(`Restoring asset failed (assetId=${assetId}): ${parseError(e)}`);
        throw e;
    });
}

/**
 * List assets
 * @param options
 * @param options.tenantType - The type of owner resource (merchant / fulfiller).
 * @param options.tenantId - The id of the resource.
 * @param [options.owner] - owner to query for.
 * @param [options.assetType] - the type of asset to query
 * @param [options.includeDeleted=false] - flag to show deleted assets or not.
 * @param [options.pageSize] - page size for responses.
 * @param [options.searchType='Description']
 * @param [options.searchText]
 * @param [options.searchEngine]
 * @returns The promise of the request.
 */
export function queryAssets(
    sdk: RenderingSDK,
    {
        assetType = DEFAULT_ASSET_TYPE,
        tenantId,
        tenantType,
        includeDeleted = false,
        pageSize = DEFAULT_PAGE_SIZE,
        owner,
        searchText,
        searchType = 'Description',
        searchEngine = 'ElasticSearch',
    }: {
        tenantType: string;
        tenantId: string;
        owner?: string;
        assetType?: string;
        includeDeleted?: boolean;
        pageSize?: number;
        searchType?: string;
        searchText?: string;
        searchEngine?: string;
    },
) {
    const queryParams = {
        assetType,
        tenantType,
        tenantId,
        pageSize: pageSize.toString(),
        includeDeleted: includeDeleted.toString(),
        ...(owner && { owner }),
        ...(searchType && searchText && { [searchType]: searchText }),
        searchEngine,
        client: 'sceneMaker',
    };

    return sdk.Assets.query(queryParams, { 'Cache-Control': 'no-cache' })
        .then((response) => {
            const assets = response._embedded.item?.map((result: TODO) => parseAsset(result));
            const nextLink = response._links.next;
            const next = nextLink && nextLink.href;
            return { scenes: assets, next };
        })
        .catch((e) => {
            logError(`Querying assets failed (queryParams=${JSON.stringify(queryParams)}): ${parseError(e)}`);
            throw e;
        });
}
